import React, { useCallback, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import FunwooAPI, {
  SendEmailPersonalConsultationV2Dto,
  SendEmailPersonalConsultationV3Dto,
  StaticOptionCategory,
  UTM,
} from '@/swagger';
import {
  Agreement,
  BasicFormValues,
  budgetOptions,
  createLectureOption,
  dDayOptions,
  EmailField,
  ExtraConsultantWay,
  floorPlanOptions,
  Input,
  interestingCityOptions,
  NameField,
  PhoneField,
  PopupSelect,
  purposesForPersonalConsultation,
  RESERVE_PERSONAL_CONSULTATION_LIKE_VALUES,
  WhereDoYouKnowUsFrom,
  whereDoYouKnowUsOptions,
} from '@/src/components/feature/ConsultantForm/ConsultantForm';
import { industryOptions } from '@/src/components/feature/ConsultantForm/ConsultantFormV2';
import clsx from 'clsx';
import Flexbox from '@/components/common/Flexbox';
import { FunwooSelect } from '@/src/components/common/FunwooSelect';
import { useAsync, useEffectOnce, useUpdateEffect } from 'react-use';
import { Button } from '@chakra-ui/react';
import OpenGraph from '@/components/OpenGraph';
import FullPage from '@/src/components/UI/FullPage';
import Image from 'next/image';
import FunwooImage from '@/components/common/FunwooImage';
import { HookFormError } from '@/src/components/common/FieldError';
import { multipleValueRequired } from '@/utils/fieldValidator';
import { isBrowser } from '@/utils/isBrowser';
import { trackLead } from '@/utils/facebookConversion';
import { useReCaptcha } from '@/lib/next-recaptcha-v3';
import { useRouter } from 'next/router';
import { MdCheckCircle } from 'react-icons/md';
import ReactIcon from '@/src/components/common/ReactIcon';
import { ConsultantFormPopup } from '@/src/components/feature/ConsultantForm/GreetingPopup';
import useMediaQuery from '@/src/hooks/useMediaQuery';

const STORAGE_FORM_KEY = '@funwoo/ConsultantForm/V3';

/**
 * The V3 UI is shared by two flows with different field sets and submit payloads:
 * - `contact` (/contact): PERSONAL_CONSULTATION_V3 — 地區 / 預算 / 房型 + 備注.
 * - `abroad` (/abroad): PERSONAL_CONSULTATION_V2 — 說明會場次(desireLecture) / 城市 / 預算, no 房型, no 備注.
 * Steps B (目的 / 購買時程 / 得知管道) and the backdrops/captions are identical for both.
 */
export type ConsultantV3Variant = 'contact' | 'abroad';

const STORAGE_FORM_KEY_BY_VARIANT: Record<ConsultantV3Variant, string> = {
  contact: STORAGE_FORM_KEY,
  abroad: `${STORAGE_FORM_KEY}/abroad`,
};

const V3ConfigContext = React.createContext<{
  variant: ConsultantV3Variant;
  lectureOptions: Array<Option>;
  lectureLoading: boolean;
  // The `desireLecture` value as of the last successful submit, captured before `reset()` wipes
  // the field back to its default — lets the success screen tell a real lecture-session sign-up
  // (讲座报名) apart from someone who picked "預約專人諮詢" from the same field.
  submittedDesireLecture: string;
  setSubmittedDesireLecture: (value: string) => void;
}>({
  variant: 'contact',
  lectureOptions: [],
  lectureLoading: false,
  submittedDesireLecture: '',
  setSubmittedDesireLecture: () => {},
});

const useV3Config = () => React.useContext(V3ConfigContext);

export enum CONSULTANT_FORM_V3_STEPS {
  'CLIENT_NEEDS_A' = 'CLIENT_NEEDS_A',
  'CLIENT_NEEDS_B' = 'CLIENT_NEEDS_B',
  'CLIENT_INFORMATION' = 'CLIENT_INFORMATION',
  'SUBMITTED' = 'SUBMITTED',
}

export interface ConsultantV3FormValues extends SendEmailPersonalConsultationV3Dto, BasicFormValues {
  phoneCountryCode: number;
  step: CONSULTANT_FORM_V3_STEPS;
  // abroad-only: the lecture / consultation session (maps to the V2 `desireLecture` field).
  desireLecture?: string;
}

const defaultFormValues: ConsultantV3FormValues = {
  type: 'PERSONAL_CONSULTATION_V3',
  interestedCity: [],
  budgetCurrency: 'USD',
  budget: '',
  interestedFloorPlan: [],
  purpose: '',
  dDay: '',
  comment: '',
  name: '',
  phone: '',
  phoneCountryCode: 886,
  email: '',
  industry: '',
  whereDoYouKnowUsFrom: '',
  availableTime: '',
  agree: false,
  desireLecture: '',
  step: CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_A,
};

const getFormCacheFromStorage = (storageKey: string): ConsultantV3FormValues => {
  if (!isBrowser()) return defaultFormValues;
  try {
    const cache = JSON.parse(localStorage.getItem(storageKey) || '{}');
    return {
      ...defaultFormValues,
      ...cache,
    };
  } catch (e) {
    console.info('parse form cache error::', e);
    return defaultFormValues;
  }
};

const checkValuesInOptions = (values: Array<string>, options: Array<Option>): Array<string> => {
  return values.filter((value) => options.some((option) => option.value === value));
};
const checkValueInOptions = (value: string, options: Array<Option>): string => {
  return options.some((option) => option.value === value) ? value : '';
};

const formValuesValidator = (formValues: ConsultantV3FormValues) => {
  // let {interestedCity, budget, interestedFloorPlan, purpose, dDay, industry, whereDoYouKnowUsFrom} = formValues
  formValues.interestedCity = checkValuesInOptions(formValues.interestedCity, interestingCityOptions);

  formValues.budget = checkValueInOptions(formValues.budget, budgetOptions);

  formValues.interestedFloorPlan = checkValuesInOptions(formValues.interestedFloorPlan, floorPlanOptions);

  formValues.purpose = checkValueInOptions(formValues.purpose, purposesForPersonalConsultation);

  formValues.dDay = checkValueInOptions(formValues.dDay, dDayOptions);

  formValues.industry = checkValueInOptions(formValues.industry, industryOptions);

  formValues.whereDoYouKnowUsFrom = checkValueInOptions(formValues.whereDoYouKnowUsFrom, whereDoYouKnowUsOptions);

  formValues.step = CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_A;

  return formValues;
};

const ConsultantFormV3: React.FC<{
  embedded?: boolean;
  variant?: ConsultantV3Variant;
}> = ({ embedded = false, variant = 'contact' }) => {
  const storageKey = STORAGE_FORM_KEY_BY_VARIANT[variant];
  const [submittedDesireLecture, setSubmittedDesireLecture] = useState('');

  // abroad needs the lecture / consultation session list (desireLecture); contact does not use it.
  // Every abroad form shows the same full session list — sessions aren't filtered per landing page.
  const { value: lectureOptions = [], loading: lectureLoading } = useAsync(async () => {
    if (variant !== 'abroad') return [];
    const { data } = await FunwooAPI.systemApi.findAllStaticOptionsByCategory({
      category: StaticOptionCategory.CONFERENCE_LOCATION,
    });
    return createLectureOption(data);
  }, [variant]);

  const methods = useForm<ConsultantV3FormValues>({
    mode: 'all',
    defaultValues: formValuesValidator(getFormCacheFromStorage(storageKey)),
  });
  const step = methods.watch('step');

  useUpdateEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  useEffectOnce(() => {
    methods.subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }: { values: ConsultantV3FormValues }) => {
        localStorage.setItem(
          storageKey,
          JSON.stringify(
            formValuesValidator({
              ...defaultFormValues,
              ...values,
            })
          )
        );
      },
    });
  });

  return (
    // @ts-ignore
    <FormProvider {...methods}>
      <V3ConfigContext.Provider
        value={{ variant, lectureOptions, lectureLoading, submittedDesireLecture, setSubmittedDesireLecture }}
      >
        <React.Fragment>
          {/* When embedded (e.g. inside the abroad page) the host page owns the page-level OpenGraph. */}
          {!embedded && (
            <OpenGraph
              title={`FUNWOO 德載國際不動產 - 專人諮詢`}
              type='funwoo.contact'
              url='https://funwoo.com.tw/contact'
              imageUrl={`${process.env.NEXT_PUBLIC_GOOGLE_CDN_URL}/largedesktop/hero/abroad_v_2.webp`}
            />
          )}
          <Content embedded={embedded} />
        </React.Fragment>
      </V3ConfigContext.Provider>
    </FormProvider>
  );
};

export default ConsultantFormV3;

/**
 * Step order used for the backdrop progress dots. The success screen is a modal shown on top
 * of the CLIENT_INFORMATION step, so it keeps that step's backdrop (per the design, step 3 and
 * the completed screen share a background).
 */
const STEP_ORDER = [
  CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_A,
  CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_B,
  CONSULTANT_FORM_V3_STEPS.CLIENT_INFORMATION,
] as const;

/**
 * Per-step backdrop image + caption — each step has its own background, and the caption text
 * follows the step too. `description` may contain newlines, rendered as line breaks.
 */
const STEP_C_BACKDROP = {
  background: '/contact/contact_bg_step_c.webp',
  title: '曼哈頓｜最奢華的摩天住宅',
  description:
    '當代紐約最奢華的摩天住宅地標。這座由知名建築師設計的巨作，以古典優雅的丰采傲立於中央公園旁。\n大樓不僅坐擁無與倫比的公園全景，更創下多項全美豪宅交易紀錄，吸引全球頂尖富豪爭相入主，成為當代極致奢華與私密生活的絕佳代名詞。',
};

const STEP_BACKDROPS: Record<CONSULTANT_FORM_V3_STEPS, { background: string; title: string; description: string }> = {
  [CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_A]: {
    background: '/contact/contact_bg_step_a.webp',
    title: '曼哈頓｜NoMad 黃金地標 艾美傳世宅邸',
    description:
      '位於諾馬德區（NoMad）地標性建築，遠眺曼哈頓中城的繁華景致。簡約低調的室內設計：由名師 Paris Forino 操刀，被《紐約時報》譽為「細節精緻如盧瓦爾河谷城堡的建築堡壘」。',
  },
  [CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_B]: {
    background: '/contact/contact_bg_step_b.webp',
    title: '曼哈頓｜第五大道摩天住宅',
    description:
      '聳立在紐約市中心的第五大道上，達到 1000 英尺的高度（88 層樓），住戶可享曼哈頓最新的私人會員俱樂部 Moss。不僅可以享受無可匹敵的生活便利，還可以融入這座城市最具創意與活力的社交圈。',
  },
  // Step 3 and the post-submit thank-you screen share the same backdrop + caption.
  [CONSULTANT_FORM_V3_STEPS.CLIENT_INFORMATION]: STEP_C_BACKDROP,
  [CONSULTANT_FORM_V3_STEPS.SUBMITTED]: STEP_C_BACKDROP,
};

/**
 * Desktop-only full-bleed backdrop: the per-step property image with a dark mask, sitting behind
 * both sections. Just the visual layer — the caption text lives in {@link ContactCaption}.
 */
const ContactDesktopBackdrop: React.FC<{ step: CONSULTANT_FORM_V3_STEPS }> = ({ step }) => {
  const { background, title } = STEP_BACKDROPS[step];

  return (
    <div className={clsx('hidden md:block', 'absolute inset-0 overflow-hidden')}>
      <Image src={background} alt={title} layout={'fill'} objectFit={'cover'} quality={90} />
      <div className={clsx('absolute inset-0', 'bg-black/30')} />
    </div>
  );
};

/**
 * Left section (desktop only): the per-step property caption + progress dots, anchored to the
 * bottom of its column. Sits beside the form section as an equal (flex-1) half.
 */
const ContactCaption: React.FC<{ step: CONSULTANT_FORM_V3_STEPS }> = ({ step }) => {
  const { title, description } = STEP_BACKDROPS[step];
  // The thank-you screen keeps the last step's dot active.
  const activeStep = step === CONSULTANT_FORM_V3_STEPS.SUBMITTED ? CONSULTANT_FORM_V3_STEPS.CLIENT_INFORMATION : step;
  const activeIndex = STEP_ORDER.indexOf(activeStep);

  return (
    <div
      className={clsx('hidden md:flex md:flex-1 md:flex-col md:justify-end', 'md:pr-[120px] md:pb-16', 'text-white')}
    >
      <p className={clsx('text-xs leading-[18px]')}>{title}</p>
      <p className={clsx('mt-4 text-xs leading-[18px] whitespace-pre-line')}>{description}</p>
      <div className={clsx('mt-6 flex gap-5')}>
        {STEP_ORDER.map((_, index) => (
          <span
            key={index}
            className={clsx('h-2.5 w-2.5 rounded-full', index === activeIndex ? 'bg-white' : 'bg-white/40')}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Shared card header (title + subtitle + divider) reused by every step.
 * - Desktop: always fully visible.
 * - Mobile/tablet: subtitle is always hidden; the title + divider only show when
 *   `visibleOnMobile` is set (the first step) — later steps rely on the page header.
 */
const V3FormHeader: React.FC<{ visibleOnMobile?: boolean }> = ({ visibleOnMobile }) => {
  const { variant } = useV3Config();
  return (
    <React.Fragment>
      <p
        className={clsx(
          visibleOnMobile ? 'block' : 'hidden',
          'md:block',
          'p-4',
          'text-gold font-medium',
          'text-xl text-center'
        )}
      >
        {variant === 'abroad' ? '說明會報名' : '立即諮詢'}
      </p>
      <p className={clsx('hidden md:block', 'text-gray700 text-center')}>
        請留下您的資訊，FUNWOO 會於 24 小時內和您聯絡！
      </p>
      <Flexbox
        align={'center'}
        justify={'center'}
        className={clsx(visibleOnMobile ? 'flex' : 'hidden', 'md:flex', 'py-4', 'mx-auto')}
      >
        <div className={clsx('h-1 w-40', 'bg-black')} />
      </Flexbox>
    </React.Fragment>
  );
};

/**
 * Shared step button reused by every step. Filled black when actionable; falls back to a gray
 * outline while `disabled` (the step's required fields are not yet valid).
 */
const V3SubmitButton: React.FC<{ primary?: boolean; disabled?: boolean; isLoading?: boolean }> = ({
  children,
  disabled,
  isLoading,
}) => {
  return (
    <Flexbox align={'center'} justify={'center'} className={clsx('py-3')}>
      <Button
        variant={'outline'}
        rounded={0}
        w={40}
        h={12}
        py={3}
        type={'submit'}
        isDisabled={disabled}
        isLoading={isLoading}
        borderColor={'black'}
        color={'white'}
        bg={'black'}
        _hover={{ bg: 'black' }}
        _disabled={{
          borderColor: 'gray.500',
          color: 'gray.500',
          bg: 'transparent',
          opacity: 1,
          cursor: 'not-allowed',
        }}
      >
        {children}
      </Button>
    </Flexbox>
  );
};

const Content: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { watch, setValue } = useFormContext<ConsultantV3FormValues>();
  const step = watch('step');
  const isStepA = step === CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_A;
  const isSubmitted = step === CONSULTANT_FORM_V3_STEPS.SUBMITTED;

  const goBack = useCallback(() => {
    if (step === CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_A) return;
    if (step === CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_B) setValue('step', CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_A);
    if (step === CONSULTANT_FORM_V3_STEPS.CLIENT_INFORMATION) setValue('step', CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_B);
  }, [step]);

  const body = (
    // Container A: full-size background image, no radius, holds the padding
    <div className={clsx('md:relative', 'md:min-h-[calc(100vh-64px)]', 'md:py-16 md:px-16 lg:px-[100px]')}>
      <ContactDesktopBackdrop step={step} />
      {/* Container B: rounded (32px), no padding, holds the two equal (flex-1) sections */}
      <div
        className={clsx(
          'relative w-full md:z-[1]',
          'md:flex md:min-h-[calc(100vh-192px)]',
          'md:rounded-[32px] md:overflow-hidden'
        )}
      >
        <ContactCaption step={step} />
        <div className={clsx('md:flex-1 md:flex md:items-stretch')}>
          <div
            className={clsx(
              'w-full md:flex md:flex-col',
              'md:bg-white md:shadow-[0_8px_32px_rgba(0,0,0,0.16)]',
              'md:px-12 lg:px-[76px] md:pt-4'
            )}
          >
            {/* When embedded there is no page header/title, so keep the card title visible on mobile for every step. */}
            {!isSubmitted && <V3FormHeader visibleOnMobile={isStepA || embedded} />}
            <FormDispatcher />
          </div>
        </div>
      </div>
    </div>
  );

  // Embedded into a host page (e.g. abroad): the host owns the FullPage chrome, so render the body only.
  // Per design, the embedded flow is forward-only — there is no header back arrow to wire goBack to.
  if (embedded) {
    return body;
  }

  return (
    <FullPage
      title={isStepA ? '專人諮詢' : '立即諮詢'}
      hasHeaderPlaceHolder
      hasPadding={false}
      hasBottomMargin={false}
      isNotSubPage={isStepA}
      frameProps={{
        maxW: 'auto',
      }}
      goBack={goBack}
    >
      {body}
    </FullPage>
  );
};

export const V3FormSectionLabel: React.FC = ({ children }) => {
  return (
    <div className={clsx('py-2', 'text-gold', 'border-b border-gold')}>
      <p>{children}</p>
    </div>
  );
};

export const V3FieldLabel: React.FC<{ required?: boolean }> = ({ children, required }) => {
  return (
    <div className={clsx('py-2')}>
      <p className={clsx('text-sm')}>
        {children}
        {required && <span className={clsx('text-red')}>*</span>}
      </p>
    </div>
  );
};

export const V3FieldContainer: React.FC = ({ children }) => {
  return <div className={clsx('space-y-2')}>{children}</div>;
};

export const useConsultantV3FormContext = () => useFormContext<ConsultantV3FormValues>();

const FormDispatcher: React.FC = () => {
  const { watch } = useConsultantV3FormContext();
  const step = watch('step');

  switch (step) {
    case CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_A:
      return <ClientNeedsA />;
    case CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_B:
      return <ClientNeedsB />;
    case CONSULTANT_FORM_V3_STEPS.CLIENT_INFORMATION:
      return <ClientInformation />;
    case CONSULTANT_FORM_V3_STEPS.SUBMITTED:
      return <ThankYou />;
    default:
      return null;
  }
};

/**
 * Post-submit thank-you screen. On desktop it replaces the form inside the card (matching the
 * "submitted" frame); on mobile/tablet it keeps the original full-screen popup, whose close
 * returns to the first step.
 */
// Real 說明會報名 (the user picked an actual session, not "預約專人諮詢" from the same field) gets its
// own completion copy — no LINE invite, and it stresses that the confirmation call is what
// finalizes the seat, not the form submission.
const useIsSeminarRegistration = () => {
  const { variant, submittedDesireLecture } = useV3Config();
  return (
    variant === 'abroad' &&
    !!submittedDesireLecture &&
    !RESERVE_PERSONAL_CONSULTATION_LIKE_VALUES.includes(submittedDesireLecture as ExtraConsultantWay)
  );
};

const ThankYouMessage: React.FC<{ exclaim?: boolean }> = ({ exclaim = false }) => {
  const isSeminarRegistration = useIsSeminarRegistration();

  if (isSeminarRegistration) {
    return (
      <React.Fragment>
        感謝您的填寫
        <br />
        FUNWOO 客服將以 {process.env.NEXT_PUBLIC_FUNWOO_TAIWAN_CELLPHONE} 致電
        <br />
        與您確認報名細節及參加人數
        <br />
        這通電話完成，才算正式完成報名
        <br />
        請留意來電，座位有限
        <br />
        以完成電話確認者為準，現場恕難安排入座
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      感謝您的填寫{exclaim && '！'}
      <br />
      專人將於 24 小時內與您聯繫
    </React.Fragment>
  );
};

const ThankYou: React.FC = () => {
  const isSeminarRegistration = useIsSeminarRegistration();
  const { reset } = useConsultantV3FormContext();
  // Below the desktop card layout (< md), keep the original popup instead of the in-card screen.
  const isBelowDesktop = useMediaQuery('(max-width: 1023px)');

  return (
    <React.Fragment>
      {/* Mobile (< md): keep the original popup shell, but with the new completion copy. */}
      <ConsultantFormPopup isOpen={isBelowDesktop} onClose={() => reset(defaultFormValues)}>
        <div className={clsx('pb-6')}>
          <div className={clsx('w-[70px] h-[70px]')}>
            <ReactIcon icon={MdCheckCircle} className={clsx('text-gold w-full h-full')} />
          </div>
        </div>
        <p className={clsx('px-4', 'text-gray700 leading-6', 'text-center')}>
          <ThankYouMessage />
        </p>
        {!isSeminarRegistration && (
          <p className={clsx('mt-6 px-4', 'text-gray700 leading-6', 'text-center')}>
            歡迎加入官方 LINE{' '}
            <a
              className={clsx('underline')}
              href={'https://liff.line.me/1645278921-kWRPP32q/?accountId=870wmpcf'}
              target={'_blank'}
              rel='noopener noreferrer'
            >
              @FUNWOO
            </a>
          </p>
        )}
      </ConsultantFormPopup>
      {/* Desktop (>= md): in-card completion screen. */}
      <div
        className={clsx('hidden md:flex md:flex-1', 'flex-col items-center justify-center text-center', 'px-4 py-16')}
      >
        <div className={clsx('w-[70px] h-[70px]')}>
          <ReactIcon icon={MdCheckCircle} className={clsx('text-gold w-full h-full')} />
        </div>
        <p className={clsx('mt-6', 'text-gray700 leading-6')}>
          <ThankYouMessage exclaim />
        </p>
        {!isSeminarRegistration && (
          <p className={clsx('mt-6', 'text-gray700 leading-6')}>
            歡迎加入官方 LINE{' '}
            <a
              className={clsx('underline')}
              href={'https://liff.line.me/1645278921-kWRPP32q/?accountId=870wmpcf'}
              target={'_blank'}
              rel='noopener noreferrer'
            >
              @FUNWOO
            </a>
          </p>
        )}
      </div>
    </React.Fragment>
  );
};

const ClientNeedsA = () => {
  const {
    control,
    setValue,
    formState: { errors, isValid },
    handleSubmit,
  } = useConsultantV3FormContext();
  const { variant, lectureOptions, lectureLoading } = useV3Config();
  const isAbroad = variant === 'abroad';

  const goNext = useCallback(
    handleSubmit(() => {
      setValue('step', CONSULTANT_FORM_V3_STEPS.CLIENT_NEEDS_B);
    }),
    [handleSubmit]
  );

  return (
    <form onSubmit={goNext} className={clsx('w-full', 'pb-8')}>
      <div className={clsx('p-4 space-y-2')}>
        <V3FormSectionLabel>置產需求</V3FormSectionLabel>
        {/* abroad leads with the lecture / consultation session; contact leads with 地區. */}
        {isAbroad && (
          <V3FieldContainer>
            <V3FieldLabel required>預約置產說明會 / 專人諮詢</V3FieldLabel>
            <PopupSelect
              darkMode={false}
              isMultiple={false}
              control={control}
              name={'desireLecture'}
              options={lectureOptions}
              rules={{ required: { value: true, message: '此欄位為必填' } }}
              label={'預約置產說明會 / 專人諮詢'}
              confirmWording={'確認'}
              isLoading={lectureLoading}
              emptyHint={'目前尚無開放場次'}
            />
            <HookFormError errors={errors} name={'desireLecture'} />
          </V3FieldContainer>
        )}
        <V3FieldContainer>
          <V3FieldLabel required>{isAbroad ? '感興趣的城市' : '地區'}</V3FieldLabel>
          <PopupSelect
            isMultiple
            darkMode={false}
            control={control}
            name={'interestedCity'}
            options={interestingCityOptions}
            rules={{ validate: multipleValueRequired }}
          />
          <HookFormError errors={errors} name={'interestedCity'} />
        </V3FieldContainer>
        <V3FieldContainer>
          <V3FieldLabel required>預算（美金）</V3FieldLabel>
          <FunwooSelect
            control={control}
            name={'budget'}
            options={budgetOptions}
            rules={{ required: { value: true, message: '此欄位為必填' } }}
          />
          <HookFormError errors={errors} name={'budget'} />
        </V3FieldContainer>
        {/* 房型 is a contact-only field; abroad (V2 payload) has no interestedFloorPlan. */}
        {!isAbroad && (
          <V3FieldContainer>
            <V3FieldLabel required>房型</V3FieldLabel>
            <PopupSelect
              isMultiple
              darkMode={false}
              control={control}
              name={'interestedFloorPlan'}
              options={floorPlanOptions}
              rules={{ validate: multipleValueRequired }}
            />
            <HookFormError errors={errors} name={'interestedFloorPlan'} />
          </V3FieldContainer>
        )}
      </div>
      <V3SubmitButton disabled={!isValid}>下一步</V3SubmitButton>
    </form>
  );
};

const ClientNeedsB = () => {
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
  } = useConsultantV3FormContext();

  const goNext = useCallback(
    handleSubmit(() => {
      setValue('step', CONSULTANT_FORM_V3_STEPS.CLIENT_INFORMATION);
    }),
    [handleSubmit]
  );

  return (
    <form onSubmit={goNext} className={clsx('pt-4 pb-8 px-4', 'w-full')}>
      <div className={clsx('space-y-2', 'pb-8')}>
        <FunwooImage
          src={'/contact/contact_client_needs_b.png'}
          alt={'client needs'}
          width={382}
          height={187}
          className={clsx('md:hidden', 'w-full')}
        />
        <V3FormSectionLabel>置產需求</V3FormSectionLabel>
        <V3FieldContainer>
          <V3FieldLabel required>目的</V3FieldLabel>
          <FunwooSelect
            control={control}
            name={'purpose'}
            options={purposesForPersonalConsultation}
            rules={{ required: { value: true, message: '此欄位為必填' } }}
          />
          <HookFormError errors={errors} name={'purpose'} />
        </V3FieldContainer>
        <V3FieldContainer>
          <V3FieldLabel required>購買時程</V3FieldLabel>
          <FunwooSelect
            control={control}
            name={'dDay'}
            options={dDayOptions}
            rules={{ required: { value: true, message: '此欄位為必填' } }}
          />
          <HookFormError errors={errors} name={'dDay'} />
        </V3FieldContainer>
        <WhereDoYouKnowUsFrom containerClassName={clsx('space-y-2')} />
      </div>
      <V3SubmitButton disabled={!isValid}>下一步</V3SubmitButton>
    </form>
  );
};

const ClientInformation = () => {
  const router = useRouter();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useConsultantV3FormContext();
  const { variant, setSubmittedDesireLecture } = useV3Config();
  const isAbroad = variant === 'abroad';

  const { executeRecaptcha } = useReCaptcha();

  const onSubmit = useCallback(
    handleSubmit(async ({ phone, phoneCountryCode, agree: _, ...formValues }: ConsultantV3FormValues) => {
      const { utm_source, utm_campaign, utm_content, utm_medium } = (router.query ?? {}) as UTM;
      const utm = {
        utm_source,
        utm_campaign,
        utm_content,
        utm_medium,
        userAgent: window.navigator.userAgent,
        ...trackLead(),
      };

      const token = await executeRecaptcha(isAbroad ? 'personal_consultation_v2' : 'personal_consultation_v3');

      // abroad submits the V2 (說明會 / 專人諮詢) payload — it carries desireLecture and now purpose,
      // and has no 房型 / 備注. contact keeps the V3 payload.
      const payload = isAbroad
        ? ({
            type: 'PERSONAL_CONSULTATION_V2',
            name: formValues.name,
            phone: `${phoneCountryCode}-${phone}`,
            email: formValues.email,
            industry: formValues.industry,
            jobLevel: '',
            lineId: '',
            whereDoYouKnowUsFrom: formValues.whereDoYouKnowUsFrom,
            desireLecture: formValues.desireLecture ?? '',
            interestedCity: formValues.interestedCity,
            budgetCurrency: formValues.budgetCurrency,
            budget: formValues.budget,
            dDay: formValues.dDay,
            purpose: formValues.purpose,
            comment: '',
            availableTime: '皆可',
            utm,
          } as SendEmailPersonalConsultationV2Dto)
        : {
            ...formValues,
            phone: `${phoneCountryCode}-${phone}`,
            utm,
          };

      await FunwooAPI.mailApi.sendEmail(payload, {
        headers: {
          Recaptcha: token,
        },
      });

      localStorage.removeItem(STORAGE_FORM_KEY_BY_VARIANT[variant]);
      // Capture before reset() wipes desireLecture back to its default — the success screen needs
      // to know whether this was a real session sign-up or a "預約專人諮詢" pick from the same field.
      setSubmittedDesireLecture(formValues.desireLecture ?? '');
      reset({ ...defaultFormValues, step: CONSULTANT_FORM_V3_STEPS.SUBMITTED });
    }, console.warn),
    [variant, setSubmittedDesireLecture]
  );

  return (
    <form className={clsx('w-full pb-8')} onSubmit={onSubmit}>
      <div className={clsx('px-4 pb-8', 'space-y-2')}>
        <V3FormSectionLabel>聯繫方式</V3FormSectionLabel>
        <V3FieldContainer>
          <NameField labelRight={'（請填寫中文全名）'} />
        </V3FieldContainer>
        <V3FieldContainer>
          <PhoneField />
        </V3FieldContainer>
        <V3FieldContainer>
          <EmailField required />
        </V3FieldContainer>
        <V3FieldContainer>
          <V3FieldLabel required>產業</V3FieldLabel>
          <FunwooSelect
            control={control}
            name={'industry'}
            options={industryOptions}
            rules={{ required: { value: true, message: '此欄位為必填' } }}
          />
          <HookFormError errors={errors} name={'industry'} />
        </V3FieldContainer>
        {/* 備注 is contact-only; the abroad V2 payload does not collect it. */}
        {!isAbroad && (
          <V3FieldContainer>
            <V3FieldLabel>備注</V3FieldLabel>
            <Input {...register('comment')} />
          </V3FieldContainer>
        )}
      </div>
      <div className={clsx('py-2 px-4')}>
        <Agreement noVerticalSpace />
      </div>
      <V3SubmitButton primary isLoading={isSubmitting}>
        送出資料
      </V3SubmitButton>
    </form>
  );
};
